import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Admission from 'models/reviews/Admission';
import { deleteAdmission } from 'services/api/admissions';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

export type DeleteAdmissionSummaryModalProps = {
  admission: Admission;
  onCancel: () => void;
};

export default function DeleteAdmissionSummaryModal({ admission, onCancel }: DeleteAdmissionSummaryModalProps) {
  const navigate = useNavigate();
  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate: deleteSummary, isPending } = useMutateWithToast({
    mutationFn: deleteAdmission,
  });

  const confirmDelete = () => {
    deleteSummary(admission.id, {
      onSuccess: () => {
        addToast({ text: 'Summary has been successfully deleted!' });
        onCancel();
        invalidateQueries({ admissions: true });
        navigate('/admissions');
      },
    });
  };

  return (
    <StandardModal colorTheme={ColorThemeName.Danger} onCancel={onCancel} title='Delete Referral?'>
      <Content>
        <Paragraph>
          <b>Are you sure you want to delete this referral?</b> Once deleted, referrals can no longer be accessed in
          Olio.
        </Paragraph>
      </Content>
      <StyledButtonGroup>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='danger' onClick={confirmDelete} loading={isPending}>
          Delete Referral
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
}

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
`;

const Content = styled.div`
  margin: 26px 0 50px;
`;

const Paragraph = styled(BodySmall)`
  display: block;
`;
